// src/stores/map.js
import { defineStore } from 'pinia'
import { useAppStore } from '@/stores/app'
import { useDataStore } from '@/stores/data'
import { trackException } from '@/services/analytics'
import googlePlacesService from '@/services/googlePlaces'

const mapboxgl = window.mapboxgl

export const useMapStore = defineStore('map', {
	state: () => ({
		accessToken: 'pk.eyJ1IjoiZmx1eGxvb3Bzb2NpYWwiLCJhIjoiY2lqZXdieWsxMDAwM3ZlbHhibXZ0aDV3ciJ9.7f6JgyWLoO8TBHbJTMcDSA',
		map: null,
		markers: [],
		layers: [],
		sources: [],
		poiMarkers: [],
		loaded: false,
		freezed: false,
		currentTripSplitMarker: null,
		currentTripMarker: null,
		currentTripSource: null,
		currentTripLayer: null,
		currentTrips: [],
		currentHome: null,
		currentWork: null,
		homeMarker: null,
		workMarker: null,
		newTripMarkers: {},
		plotTripsWhenLoaded: false,
		currentEditTrip: null,
		currentEditTripIsNew: false,
		longPressTimeout: null,
		longPressStartPoint: null,
		longPressDuration: 700,
		onCoordinateChanged: null,
	}),

	actions: {
		refresh() {
			console.warn('map: refresh')
			const center = this.map.getCenter()
			const bearing = this.map.getBearing()
			const zoom = this.map.getZoom()
			this.map.remove()
			this.map = null
			this.initMap(center, bearing, zoom, () => {
				const dataStore = useDataStore()
				const trips = dataStore.currentJourney?.trips ?? []
				this.plotTripsInner(trips, -1, true)
			})
		},
		initMap(center = [12.04, 55.82], bearing = 0, zoom = 1.9, loadedCallback = null) {
			try {
				mapboxgl.accessToken = this.accessToken

				this.map = new mapboxgl.Map({
					container: 'map',
					style: 'https://api.mapbox.com/styles/v1/fluxloopsocial/cjxk8h58q4lbx1cp8j9bt4mx0?access_token=' + mapboxgl.accessToken,
					center: center,
					pitch: 0,
					bearing: bearing,
					zoom: zoom,
				})

				// Initialize Google Places services
				googlePlacesService.init()

				this.map.on('load', () => {
					this.loaded = true
					if (this.plotTripsWhenLoaded && this.currentTrips.length) {
						this.plotTrips(this.currentTrips)
					}
					if (loadedCallback) {
						loadedCallback()
					}
				})
				const appStore = useAppStore()
				this.map.on('movestart', () => {
					if (appStore) appStore.expandedHelp = false
				})

				this.setupLongPressDetection()
			} catch (error) {
				console.error('Error initializing map:', error)
			}
		},
		isMapVisible() {
			const mapContainer = document.getElementById('map')
			const canvas = this.map.getCanvas()
			const isVisible = mapContainer && canvas && mapContainer.offsetWidth > 0 && mapContainer.offsetHeight > 0 && canvas.width > 0 && canvas.height > 0 && this.map.isStyleLoaded()
			console.warn('isMapVisible', isVisible, mapContainer, canvas)
			return isVisible
		},
		simulateBrokenMap() {
			const canvas = this.map.getCanvas()
			const parent = canvas.parentNode
			parent.removeChild(canvas)
			setTimeout(() => parent.appendChild(canvas), 0)
			console.log('Map broken state simulated')
		},
		async mapboxSearch(query) {
			try {
				return await googlePlacesService.search(query)
			} catch (error) {
				console.error('Places Autocomplete error:', error)
				return []
			}
		},

		// async mapboxRetrieveSuggestion(suggestion) {
		// 	console.warn('Map: mapboxRetrieveSuggestion', suggestion)
		// 	try {
		// 		return await googlePlacesService.retrieveSuggestionDetails(suggestion)
		// 	} catch (error) {
		// 		console.error('Place Details error:', error)
		// 		return { features: [] }
		// 	}
		// },

		async getPlaceForLocation(location) {
			try {
				return await googlePlacesService.getPlaceForLocation(location)
			} catch (error) {
				console.error('Place Search error:', error)
				return {
					title: 'Ukjent sted',
					subTitle: '',
				}
			}
		},

		formatLat(latitude) {
			const latAbs = Math.abs(latitude)
			const latDeg = Math.floor(latAbs)
			const latMin = Math.floor((latAbs - latDeg) * 60)
			const latSec = Math.round((latAbs - latDeg - latMin / 60) * 3600 * 100) / 100
			const latCardinal = latitude >= 0 ? 'N' : 'S'
			return `${latDeg}° ${latMin}' ${latSec}" ${latCardinal}`
		},

		formatLng(longitude) {
			const lngAbs = Math.abs(longitude)
			const lngDeg = Math.floor(lngAbs)
			const lngMin = Math.floor((lngAbs - lngDeg) * 60)
			const lngSec = Math.round((lngAbs - lngDeg - lngMin / 60) * 3600 * 100) / 100
			const lngCardinal = longitude >= 0 ? 'Ø' : 'V'
			return `${lngDeg}° ${lngMin}' ${lngSec}" ${lngCardinal}`
		},

		setCoordinateChangeListener(callback) {
			this.onCoordinateChanged = callback
		},

		longPressHandler(lngLat) {
			console.warn('longPressHandler', lngLat)
			if (this.currentEditTrip) {
				const dataStore = useDataStore()
				this.currentEditTrip[dataStore.currentLocationType].latitude = lngLat.lat
				this.currentEditTrip[dataStore.currentLocationType].longitude = lngLat.lng
				this.currentEditTrip[dataStore.currentLocationType].type = 'gps'
				this.currentEditTrip[dataStore.currentLocationType].title = 'GPS koordinat'
				this.currentEditTrip[dataStore.currentLocationType].subTitle = 'Breddegrad: ' + lngLat.lat.toFixed(5) + ', Lengdegrad: ' + lngLat.lng.toFixed(5)
			}
			this.onCoordinateChanged(lngLat)
		},

		setupLongPressDetection() {
			this.map.on('touchstart', (e) => {
				console.log('touchstart', e)
				if (e.originalEvent.touches.length === 1) {
					this.longPressStartPoint = e.point
					this.longPressTimeout = setTimeout(() => {
						console.log('long press detected')
						if (this.onCoordinateChanged) {
							this.longPressHandler(e.lngLat)
						}
						// Trigger CustomEvent in window
						window.dispatchEvent(new CustomEvent('map-location', { detail: { latitude: e.lngLat.lat, longitude: e.lngLat.lng } }))
					}, this.longPressDuration)
				}
			})

			this.map.on('touchmove', (e) => {
				if (this.longPressTimeout && this.longPressStartPoint) {
					const moveThreshold = 5
					const dx = e.point.x - this.longPressStartPoint.x
					const dy = e.point.y - this.longPressStartPoint.y
					const distance = Math.sqrt(dx * dx + dy * dy)

					if (distance > moveThreshold) {
						clearTimeout(this.longPressTimeout)
						this.longPressTimeout = null
						this.longPressStartPoint = null
					}
				}
			})

			this.map.on('touchend', (e) => {
				console.log('touchend', e)
				if (this.longPressTimeout) {
					clearTimeout(this.longPressTimeout)
					this.longPressTimeout = null
					this.longPressStartPoint = null
				}
			})
		},

		getPopoverContent(trip) {
			const dataStore = useDataStore()
			const mode = dataStore.getTransportMode(trip.activity_name)
			const distance = dataStore.formatDistance(trip.distance)
			const duration = dataStore.formatDurationFromSeconds(trip.duration)
			return `<h4>${mode.desc}</h4><strong>Varighet:</strong> ${duration}<br/><strong>Start:</strong> ${new Date(trip.departure_timestamp).toLocaleTimeString()}<br/><strong>Slutt:</strong> ${new Date(trip.arrival_timestamp).toLocaleTimeString()}<br/><strong>Avstand:</strong> ${distance}`
		},

		getTransportMode(trip) {
			const dataStore = useDataStore()
			return dataStore.getTransportMode(trip)
		},

		clearNewTripMarkes() {
			if (this.newTripMarkers.origin) {
				this.newTripMarkers.origin.remove()
				this.newTripMarkers.origin = null
			}
			if (this.newTripMarkers.destination) {
				this.newTripMarkers.destination.remove()
				this.newTripMarkers.destination = null
			}
		},

		moveTripMarker(trip, locationType, place) {
			console.log('moveTripMarker', trip, locationType, place)

			// Clone trip locations
			const tripLocations = trip.locations.map((loc) => ({ ...loc }))

			// Update locations array
			tripLocations[locationType === 'origin' ? 0 : tripLocations.length - 1].latitude = place.latitude
			tripLocations[locationType === 'origin' ? 0 : tripLocations.length - 1].longitude = place.longitude
			tripLocations[locationType === 'origin' ? 0 : tripLocations.length - 1].hacc = 1

			// Move marker to new location
			this.markers.forEach((marker) => {
				const el = marker.getElement()
				var targetId = locationType === 'origin' ? 'start' : 'stop'
				targetId += '-' + trip.id
				if (el.id === targetId) {
					marker.setLngLat([place.longitude, place.latitude])
				}
			})

			// Create updated source with changed coordinates
			const routeSource = this.getRouteSource(trip.id, tripLocations)
			// Update the source data
			this.map.getSource(`route-${trip.id}`).setData(routeSource.data)

			// Fly to new location
			this.map.flyTo({ center: [place.longitude, place.latitude], zoom: 14, speed: 0.5 })
		},

		addPlaceMarker(place) {
			this.removeAllMarkers()
			const el = document.createElement('div')
			el.id = 'place-' + place.id
			el.className = 'marker place'
			el.style.zIndex = '20'

			const marker = new mapboxgl.Marker({ element: el }).setLngLat(place.coordinate).addTo(this.map)

			if (place && !this.currentEditTrip) {
				const popup = new mapboxgl.Popup({ offset: 25 }).setHTML(this.getPopoverContent(place))
				marker.setPopup(popup)
				el.addEventListener('click', () => popup.addTo(this.map))
			}

			this.markers.push(marker)
		},

		addStartMarker(mode, start, id, trip, zIndex = 20) {
			const el = document.createElement('div')
			el.id = 'start-' + id
			el.className = `marker start ${mode.type}`
			el.innerHTML = '<i class="fas fa-circle fa-3x"></i>'
			el.style.zIndex = '' + zIndex

			const marker = new mapboxgl.Marker({ element: el }).setLngLat([start.longitude, start.latitude]).addTo(this.map)

			if (trip && !this.currentEditTrip) {
				const popup = new mapboxgl.Popup({ offset: 25 }).setHTML(this.getPopoverContent(trip))
				marker.setPopup(popup)
				el.addEventListener('click', () => popup.addTo(this.map))
			}

			return marker
		},

		addStopMarker(mode, end, id, trip) {
			const el = document.createElement('div')
			el.id = 'stop-' + id
			el.className = `marker stop ${mode.type}`
			//el.style.borderColor = mode.color
			el.style.zIndex = '10'

			const marker = new mapboxgl.Marker({ element: el }).setLngLat([end.longitude, end.latitude]).addTo(this.map)

			if (trip && !this.currentEditTrip) {
				const popup = new mapboxgl.Popup({ offset: 25 }).setHTML(this.getPopoverContent(trip))
				marker.setPopup(popup)
			}

			return marker
		},

		removeCurrentSplitLocationMarker() {
			if (this.currentTripSplitMarker) {
				this.currentTripSplitMarker.remove()
				this.currentTripSplitMarker = null
			}
		},

		flyToLocation(location, maxZoom = 16, duration = 1000) {
			if (!location || !this?.map) return

			let appStore, dataStore, bounds, bottomPadding

			try {
				appStore = useAppStore()
				dataStore = useDataStore()

				if (appStore.bottomSheet.currentState == 'fullscreen') return

				const toNum = (v) => (typeof v === 'string' ? Number(v.replace(',', '.').trim()) : v)

				let lng = toNum(location.lng ?? location.longitude ?? location.lon)
				let lat = toNum(location.lat ?? location.latitude)

				// Validate coordinates are finite AND within valid ranges
				if (!Number.isFinite(lng) || !Number.isFinite(lat) || Number.isNaN(lng) || Number.isNaN(lat) || Math.abs(lng) > 180 || Math.abs(lat) > 90) {
					console.warn('flyToLocation: invalid coordinates', {
						location,
						lng,
						lat,
						reason: !Number.isFinite(lng) || !Number.isFinite(lat) ? 'not finite' : Number.isNaN(lng) || Number.isNaN(lat) ? 'NaN' : Math.abs(lng) > 180 ? 'longitude out of range' : Math.abs(lat) > 90 ? 'latitude out of range' : 'unknown',
					})
					return
				}

				bounds = new mapboxgl.LngLatBounds([lng, lat], [lng, lat])

				var currentBottomSheetHeight = appStore.bottomSheet.currentHeight ?? 0
				bottomPadding = currentBottomSheetHeight + 50

				if (appStore.bottomSheet.currentState == 'hidden') bottomPadding = 100

				this.map.fitBounds(bounds, {
					padding: {
						top: 50,
						left: 50,
						right: 50,
						bottom: bottomPadding,
					},
					maxZoom: maxZoom,
					duration: duration,
				})
			} catch (err) {
				console.error('flyToLocation error:', err, { location, bounds, bottomPadding, maxZoom, duration })
				// Lets just ignore any errors here for now
				// trackException(
				// 	err,
				// 	{
				// 		type: 'map_fit_bounds_error',
				// 		object: {
				// 			location: typeof location != 'undefined' ? JSON.stringify(location) : null,
				// 			bottomPadding: typeof bottomPadding != 'undefined' ? bottomPadding : null,
				// 			bounds: typeof bounds != 'undefined' ? JSON.stringify(bounds) : null,
				// 			maxZoom,
				// 			duration,
				// 		},
				// 	},
				// 	appStore,
				// 	dataStore,
				// )
			}
		},

		updateCurrentSplitLocationMarker(trip, locationRangeIndex) {
			const sourceId = `route-${trip.id}`
			const source = this.map.getSource(sourceId)
			const coordinates = source._data.geometry.coordinates
			const originalIndexes = source._data.properties.originalIndexes

			let locationIndex = Math.round((coordinates.length * locationRangeIndex) / 100)
			if (locationRangeIndex == 100) locationIndex = coordinates.length - 1

			const locationCenter = coordinates[locationIndex]

			if (!this.currentTripSplitMarker) {
				const el = document.createElement('div')
				el.id = 'current-split-location'
				el.className = 'marker split'
				el.innerHTML = '<i class="fas fa-circle fa-3x"></i>'
				el.style.zIndex = '30'

				this.currentTripSplitMarker = new mapboxgl.Marker({ element: el }).setLngLat(locationCenter).addTo(this.map)
			} else {
				this.currentTripSplitMarker.setLngLat(locationCenter)
			}

			this.flyToLocation(locationCenter, 15, 800)

			return {
				locationIndex: originalIndexes[locationIndex],
				location: trip.locations[originalIndexes[locationIndex]],
			}
		},

		getCoordinatesForGeoJson(coordinates) {
			return coordinates.filter((coord, i) => coord.hacc < 100 || i === 0 || i === coordinates.length - 1).map((coord, i) => ({ ...coord, originalIndex: i }))
		},

		getRouteSource(id, coordinates) {
			const filteredLocations = this.getCoordinatesForGeoJson(coordinates)
			const filteredCoordinates = filteredLocations.map((coord) => [coord.longitude, coord.latitude])
			const originalIndexes = filteredLocations.map((coord) => coord.originalIndex)

			return {
				type: 'geojson',
				data: {
					type: 'Feature',
					properties: {
						id,
						originalIndexes,
					},
					geometry: {
						type: 'LineString',
						coordinates: filteredCoordinates,
					},
				},
			}
		},

		getRouteLayer(id, mode) {
			var color = mode.color
			const layerData = {
				id: `route-${id}`,
				type: 'line',
				source: `route-${id}`,
				paint: {
					'line-color': color ?? 'gray',
					'line-width': 3,
					'line-opacity': mode.deleted ? 0.5 : 1,
				},
			}
			return layerData
		},

		computeAngle(start, end) {
			const deltaY = end.latitude - start.latitude
			const deltaX = end.longitude - start.longitude
			const radian = Math.atan2(deltaY, deltaX)
			return radian * (180 / Math.PI)
		},

		normalizeBearing(bearing) {
			while (bearing > 180) bearing -= 360
			while (bearing < -180) bearing += 360
			return bearing
		},

		plotHomeWork(home, work) {
			if (home && work) {
				this.currentHome = home
				this.currentWork = work
			}
			if (this.currentHome && this.currentHome.center) {
				this.homeMarker = this.addStartMarker({ type: 'home', bgcolor: 'blue' }, this.currentHome.center, 'home', null, 5)
			}
			if (this.currentWork && this.currentWork.center) {
				this.workMarker = this.addStartMarker({ type: 'work', bgcolor: 'blue' }, this.currentWork.center, 'work', null, 5)
			}
		},

		plotCurrentEdit() {
			const dataStore = useDataStore()
			this.plotTripsInner([dataStore.currentEditTrip], -1)
		},

		setCurrentEditTripLocationForPlace(place, locationType) {
			console.warn('GeoCoder: setMapStoreCurrentEditTrip', place)

			if (this.currentEditTrip && mapStore.currentEditTrip[locationType]) {
				// Update origin or destination location
				this.currentEditTrip[locationType].latitude = place.latitude
				this.currentEditTrip[locationType].longitude = place.longitude
				this.currentEditTrip[locationType].title = place.title
				this.currentEditTrip[locationType].subTitle = place.subTitle
				this.currentEditTrip[locationType].type = place.type
				this.currentEditTrip[locationType].place_id = place.id
				this.currentEditTrip[locationType].categories = place.categories
				this.currentEditTrip[locationType].placeTypes = place.placeTypes

				// Update locations array
				if (locationType === 'origin') {
					this.currentEditTrip.locations[0].latitude = place.latitude
					this.currentEditTrip.locations[0].longitude = place.longitude
				}
				if (locationType === 'destination') {
					this.currentEditTrip.locations[mapStore.currentEditTrip.locations.length - 1].latitude = place.latitude
					this.currentEditTrip.locations[mapStore.currentEditTrip.locations.length - 1].longitude = place.longitude
				}

				// Plot new or edit trip
				this.plotTripsInner([this.currentEditTrip], 0, false)
			} else {
				console.warn('map: setCurrentEditTripLocationForPlace', 'No currentEditTrip')
			}
		},

		plotTripsInner(trips, showTripIndex = -1, fitBounds = true) {
			this.currentTrips = trips
			this.removeAllMarkers()
			this.removeAllSourcesAndLayers()

			trips.forEach((trip) => {
				// Lets not plot deleted trips
				if (!trip || trip?.deleted) {
					return
				}

				var mode = this.getTransportMode(trip.activity_name)

				const start = trip.origin?.latitude ? trip.origin : null
				const end = trip.destination?.latitude ? trip.destination : null

				if (start && end) {
					const sourceId = `route-${trip.id}`
					const layerId = `route-${trip.id}`

					// Makes sure to remove existing source and layer
					if (this.map.getSource(sourceId)) {
						if (this.map.getLayer(layerId)) {
							this.map.removeLayer(layerId)
						}
						this.map.removeSource(sourceId)
					}

					const tripSource = this.getRouteSource(trip.id, trip.locations)
					this.map.addSource(sourceId, tripSource)
					this.sources.push(tripSource)

					const tripLayer = this.getRouteLayer(trip.id, mode)
					this.map.addLayer(tripLayer)
					this.layers.push(tripLayer)
				}

				if (start) {
					const startMarker = this.addStartMarker(mode, start, trip.id, trip)
					this.markers.push(startMarker)
				}

				if (end) {
					const endMarker = this.addStopMarker(mode, end, trip.id, trip)
					this.markers.push(endMarker)
				}
			})

			if (showTripIndex >= 0) {
				this.showTrip(trips[showTripIndex], fitBounds)
			} else {
				this.showTrips(trips, fitBounds)
			}
		},

		plotTrips(showTripIndex = -1) {
			console.warn('map: plotTrips')
			if (this.freezed) return
			if (!this.loaded) {
				this.plotTripsWhenLoaded = true
				return
			}
			const dataStore = useDataStore()
			const trips = dataStore.currentJourney?.trips ?? []
			this.plotTripsInner(trips, showTripIndex)
		},

		showTrip(trip, fitBounds) {
			if (!trip) return
			this.showTrips([trip], fitBounds)
		},

		showCurrentTrips(fitBounds) {
			const dataStore = useDataStore()
			if (dataStore.currentEditTrip != null && Object.keys(dataStore.currentEditTrip).length > 0) {
				this.showTrips([dataStore.currentEditTrip], fitBounds)
			} else {
				this.showTrips(this.currentTrips, fitBounds)
			}
		},

		showTrips(trips, fitBounds = true) {
			const dataStore = useDataStore()
			console.warn('map: showTrips', trips, fitBounds)

			if (!Array.isArray(trips) || !trips.length || this.freezed) {
				console.warn('map: No trips provided or map is freezed. Exiting showTrips.')
				return
			}

			const bounds = new mapboxgl.LngLatBounds()
			const uniquePoints = new Set()

			// inline helpers
			const toNum = (v) => {
				if (v == null) return NaN
				const s = String(v).replace(',', '.').trim()
				return Number(s)
			}
			const isFiniteNum = (n) => Number.isFinite(n)
			const validLatLng = (lat, lng) => isFiniteNum(lat) && isFiniteNum(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180
			const key = (lat, lng) => `${lat.toFixed(6)},${lng.toFixed(6)}`

			const extendBoundsSafely = (latIn, lngIn, sourceInfo = 'unknown') => {
				const lat = toNum(latIn)
				const lng = toNum(lngIn)
				if (validLatLng(lat, lng)) {
					bounds.extend([lng, lat])
					uniquePoints.add(key(lat, lng))
					return true
				} else {
					console.warn(`map: Invalid coordinate from ${sourceInfo}: lat=${latIn}, lng=${lngIn}`)
					return false
				}
			}

			for (const trip of trips) {
				if (trip?.origin) extendBoundsSafely(trip.origin.latitude, trip.origin.longitude, `trip ${trip.id} origin`)
				if (trip?.destination) extendBoundsSafely(trip.destination.latitude, trip.destination.longitude, `trip ${trip.id} destination`)
				;(trip?.locations ?? []).forEach((loc, idx) => extendBoundsSafely(loc.latitude, loc.longitude, `trip ${trip.id} location ${idx}`))
			}

			if (bounds.isEmpty()) {
				console.warn('map: No valid coordinates found to establish map bounds. Skipping fitBounds.')
				return
			}

			const isSinglePoint = uniquePoints.size === 1

			if (fitBounds) {
				const appStore = useAppStore()
				if (appStore.bottomSheet.currentState !== 'fullscreen') {
					const h = toNum(appStore.bottomSheet.currentHeight)
					let bottomPadding = Number.isFinite(h) && h > 0 ? h + 50 : 100
					if (appStore.bottomSheet.currentState === 'hidden') bottomPadding = 100

					const padding = { top: 50, left: 50, right: 50, bottom: bottomPadding }

					try {
						if (isSinglePoint) {
							const center = bounds.getCenter()
							this.map.easeTo({
								center,
								zoom: Math.min(this.map.getZoom() || 14, 16),
								bearing: 0,
								duration: 1000,
								padding,
							})
						} else {
							this.map.fitBounds(bounds, {
								padding,
								maxZoom: 16,
								bearing: 0, // keep north-up; swap in your dynamic bearing if wanted
								duration: 1000,
							})
						}
					} catch (e) {
						console.warn('map: fitBounds/easeTo failed, skipping', e)
					}
				}
			}

			// Reset layers and markers
			this.resetLayersAndMarkers(trips)
			if (trips.length === 1) this.highlightTrip(trips[0])
			else {
				this.resetLayerVisibility(trips)
				this.resetMarkerVisibility()
			}

			// Joinable trips styling
			const joinableTrips = trips.filter((t) => t.join)
			if (joinableTrips.length > 0) {
				joinableTrips.forEach((trip) => {
					if (!trip.id) return
					const routeId = `route-${trip.id}`
					if (this.map.getLayer(routeId)) {
						this.map.setPaintProperty(routeId, 'line-color', '#007bff')
						this.map.setPaintProperty(routeId, 'line-width', 5)
						this.map.setPaintProperty(routeId, 'line-opacity', 1)
						const startMarker = document.getElementById('start-' + trip.id)
						const stopMarker = document.getElementById('stop-' + trip.id)
						if (startMarker) {
							startMarker.classList.add('join')
							startMarker.style.zIndex = '40'
							startMarker.style.opacity = '1'
						} else console.warn(`map: Start marker not found for trip ${trip.id}`)
						if (stopMarker) {
							stopMarker.classList.add('join')
							stopMarker.style.zIndex = '40'
							stopMarker.style.opacity = '1'
						} else console.warn(`map: Stop marker not found for trip ${trip.id}`)
					}
				})
				this.currentTrips
					.filter((t) => !t.join)
					.forEach((trip) => {
						if (!trip.id) return
						const routeId = `route-${trip.id}`
						if (this.map.getLayer(routeId)) this.map.setPaintProperty(routeId, 'line-opacity', 0)
					})
			}
		},

		resetLayersAndMarkers(trips) {
			console.log('map: resetLayersAndMarkers', trips)
			const dataStore = useDataStore()
			trips.forEach((trip) => {
				if (trip.id) {
					const routeId = `route-${trip.id}`
					if (this.map.getLayer(routeId)) {
						const mode = dataStore.getTransportMode(trip.activity_name)
						var color = mode?.color ?? 'gray'
						this.map.setPaintProperty(routeId, 'line-color', color)
						this.map.setPaintProperty(routeId, 'line-width', 2)
						this.map.setPaintProperty(routeId, 'line-opacity', 1)
					}
					var startMarker = document.getElementById('start-' + trip.id)
					if (startMarker) {
						startMarker.classList.remove('join')
						startMarker.style.zIndex = '40'
						startMarker.style.opacity = '1'
					}
					var stopMarker = document.getElementById('stop-' + trip.id)
					if (stopMarker) {
						stopMarker.classList.remove('join')
					}
				}
			})
		},

		updateMapForEdit() {
			console.warn('map: updateMapForEdit', this.currentEditTrip)
			if (this.currentEditTrip && this.currentEditTrip.id + '') {
				this.highlightTrip(this.currentEditTrip)
			}
		},

		clearMap(removeHomeWork = false) {
			this.currentTrips = []
			this.removeAllMarkers()
			this.removeAllSourcesAndLayers()
			if (removeHomeWork) {
				if (this.homeMarker) {
					this.homeMarker.remove()
					this.homeMarker = null
				}
				if (this.workMarker) {
					this.workMarker.remove()
					this.workMarker = null
				}
			}
		},

		removeAllMarkers() {
			this.markers.forEach((marker) => marker.remove())
			this.markers = []

			if (this.currentTripSplitMarker) {
				this.currentTripSplitMarker.remove()
				this.currentTripSplitMarker = null
			}
		},

		removeAllSourcesAndLayers(keepId) {
			// Remove each layer
			this.layers.forEach((layer) => {
				if (layer.id !== keepId && this.map.getLayer(layer.id)) {
					this.map.removeLayer(layer.id)
				}
			})
			// Clear the layers array
			this.layers = []

			this.sources.forEach((source) => {
				const sourceId = `route-${source.data.properties.id}`
				if (sourceId !== keepId && this.map.getSource(sourceId)) {
					// Remove each source
					this.map.removeSource(sourceId)
				}
			})

			// Clear the sources array
			this.sources = []
		},

		resetMarkerVisibility() {
			this.markers.forEach((marker) => {
				const el = marker.getElement()
				const isStartMarker = el.id.includes('start')
				el.style.zIndex = isStartMarker ? '20' : '10'
				el.style.opacity = '1'
			})
		},

		resetLayerVisibility(trips) {
			this.layers.forEach((layer) => {
				this.map.setPaintProperty(layer.id, 'line-width', 3)
				this.map.setPaintProperty(layer.id, 'line-opacity', 1)
			})
		},

		setLayerVisibility(selectedTripId) {
			this.layers.forEach((layer) => {
				if (layer.id !== selectedTripId) {
					this.map.setPaintProperty(layer.id, 'line-width', 2)
					this.map.setPaintProperty(layer.id, 'line-opacity', 0.2)
				} else {
					this.map.setPaintProperty(layer.id, 'line-width', 5)
					this.map.setPaintProperty(layer.id, 'line-opacity', 1)
				}
			})
		},

		// Highlight and bring single trip to front, hide the others
		highlightTrip(trip) {
			console.warn('map: highlightTrip', trip)

			const tripId = trip.id + ''
			if (tripId) {
				const dataStore = useDataStore()
				const routeId = `route-${tripId}`

				// Set the line wider for the selected trip
				if (this.map.getLayer(routeId)) {
					this.map.setPaintProperty(routeId, 'line-width', 5)
					this.setLayerVisibility(routeId)
				}

				// Bring the highlighted trip layer to front
				if (this.map.getLayer(routeId)) {
					this.map.moveLayer(routeId)
				}

				// Show or hide home and work markers
				if (this.homeMarker && this.workMarker) {
					if (this.currentEditTrip) {
						this.homeMarker.getElement().style.opacity = '0.5'
						this.workMarker.getElement().style.opacity = '0.5'
					} else {
						this.homeMarker.getElement().style.opacity = '1'
						this.workMarker.getElement().style.opacity = '1'
					}
				}

				// Hide or show markers
				this.markers.forEach((marker) => {
					const el = marker.getElement()
					const isStartMarker = el.id.includes('start')

					// Show the selected trip markers
					if (el.id == 'start-' + tripId || el.id == 'stop-' + tripId) {
						el.style.zIndex = isStartMarker ? '40' : '30'
						el.style.opacity = '1'
						if (this.currentEditTrip && Object.keys(this.currentEditTrip).length > 1 && this.currentEditTrip.id + '' == tripId) {
							this.handleMarkerEdit(tripId, marker, isStartMarker, dataStore)
						}
					} else {
						// Hide other markers
						el.style.zIndex = isStartMarker ? '20' : '10'
						el.style.opacity = '0.2'
						marker.setDraggable(false)
						marker.off('drag')
						marker.off('dragend')
					}
				})
			}
		},

		updateTripOriginDestination(trip, lngLat, locationType) {
			trip[locationType].title = 'GPS koordinat'
			trip[locationType].subTitle = 'Breddegrad: ' + lngLat.lat.toFixed(5) + ', Lengdegrad: ' + lngLat.lng.toFixed(5)
			trip[locationType].latitude = lngLat.lat
			trip[locationType].longitude = lngLat.lng
			trip[locationType].type = 'gps'
			var locationTypeIndex = locationType === 'origin' ? 0 : trip.locations.length - 1
			trip.locations[locationTypeIndex].latitude = lngLat.lat
			trip.locations[locationTypeIndex].longitude = lngLat.lng
			trip.locations[locationTypeIndex].hacc = 1
		},

		handleMarkerEdit(tripId, marker, isStartMarker, dataStore) {
			console.log('handleMarkerEdit', tripId, marker, isStartMarker, dataStore.currentLocationType)

			const el = marker.getElement()
			marker.setDraggable(true)

			marker.on('dragend', () => {
				const lngLat = marker.getLngLat()
				if (isStartMarker) dataStore.locationType = 'origin'
				else dataStore.currentLocationType = 'destination'
				if (isStartMarker) this.currentEditTrip.origin_timestamp = new Date().toISOString()
				else this.currentEditTrip.destination_timestamp = new Date().toISOString()
				this.onCoordinateChanged(lngLat)
			})

			el.addEventListener('click', () => {
				if (isStartMarker) dataStore.currentLocationType = 'origin'
				else dataStore.currentLocationType = 'destination'
				console.log('marker click', tripId, marker, dataStore.currentLocationType)
				// TODO: Add selected class to marker?
			})

			marker.on('drag', () => {
				const lngLat = marker.getLngLat()
				// Update the trip
				if (isStartMarker) this.updateTripOriginDestination(this.currentEditTrip, lngLat, 'origin')
				else this.updateTripOriginDestination(this.currentEditTrip, lngLat, 'destination')
				// Create updated source with changed coordinates
				const routeSource = this.getRouteSource(this.currentEditTrip.id, this.currentEditTrip.locations)
				// Update the source data
				const sourceId = `route-${this.currentEditTrip.id}`
				const currentSource = this.map.getSource(sourceId)
				if (currentSource) currentSource.setData(routeSource.data)
			})
		},

		clearPoiLayers() {
			const layersToRemove = ['poi-polygon-layer', 'poi-icon-layer']
			layersToRemove.forEach((layerId) => {
				if (this.map.getLayer(layerId)) {
					this.map.removeLayer(layerId)
				}
			})

			if (this.map.getSource('poi-source')) {
				this.map.removeSource('poi-source')
			}

			// Remove any poi markers
			if (this.poiMarkers) {
				this.poiMarkers.forEach((marker) => marker.remove())
				this.poiMarkers = []
			}
		},

		plotDestinationPois(pois) {
			this.clearPoiLayers()
			if (!pois?.length) return

			this.poiMarkers = []

			// Add only markers for POI icons
			pois.forEach((poi) => {
				if (!poi.location || !poi.iconUrl || poi.type == 'place:other' || poi.type == 'place:work' || poi.type == 'place:home') return
				console.log('plotDestinationPois', poi)

				const el = document.createElement('div')
				el.className = 'poi-marker'
				el.style.backgroundImage = `url(${poi.iconUrl})`
				el.style.width = '24px'
				el.style.height = '24px'
				el.style.backgroundSize = '1rem'
				el.style.backgroundRepeat = 'no-repeat'
				el.style.backgroundPosition = 'center'

				const marker = new mapboxgl.Marker({
					element: el,
					anchor: 'center',
				})
					.setLngLat([poi.location.longitude, poi.location.latitude])
					.addTo(this.map)

				this.poiMarkers.push(marker)
			})
		},

		plotSelectedPoiPolygon(poi) {
			// Clear any existing polygon
			if (this.map.getLayer('selected-poi-polygon')) {
				this.map.removeLayer('selected-poi-polygon')
			}
			if (this.map.getSource('selected-poi-source')) {
				this.map.removeSource('selected-poi-source')
			}

			if (!poi?.polylines?.length) return

			// Add source and polygon layer for selected POI
			this.map.addSource('selected-poi-source', {
				type: 'geojson',
				data: {
					type: 'Feature',
					geometry: {
						type: 'Polygon',
						coordinates: [poi.polylines[0].map((coord) => [coord.longitude, coord.latitude])],
					},
				},
			})

			this.map.addLayer({
				id: 'selected-poi-polygon',
				type: 'fill',
				source: 'selected-poi-source',
				paint: {
					'fill-color': '#ff0000', // #007bff
					'fill-opacity': 1,
					'fill-outline-color': '#ffffff',
				},
			})
		},
	},
})
